import { createAction } from '@reduxjs/toolkit';
import type { SerializedEditorState } from 'lexical';

import type { LanguageEnum } from 'js/src/client';
import type {
    LexicalEditorsDataType,
    TranscriptTranslations,
} from 'js/src/data/transcript/transcript-types';
import { TRANSCRIPT_TYPES } from 'js/src/data/transcript/transcript-types';
import type { ImmutableMap } from 'js/src/libs/consts';

export const setTranscriptTabVisibility = createAction<boolean>(
    TRANSCRIPT_TYPES.SET_TRANSCRIPT_TAB_VISIBILITY,
);

export const disableTranscriptOverlays = createAction<{
    sceneGroupId: string;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.DISABLE_OVERLAYS);

export const toggleOriginal = createAction<{
    sceneGroupId: string;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.TOGGLE_ORIGINAL);

export const setTranscriptLanguage = createAction<{
    sceneGroupId: string;
    language: LanguageEnum;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.SET_TRANSCRIPT_LANGUAGE);

export const storeRawTranscript = createAction<{
    sceneGroupId: string;
    editorState: SerializedEditorState;
    isOriginal: boolean;
    language?: LanguageEnum;
    translations?: TranscriptTranslations;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.STORE_TRANSCRIPT);

export const deleteTranscript = createAction<{
    sceneGroupId: string;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.DELETE_TRANSCRIPT);

export const setSceneConversionModalVisible = createAction<boolean>(
    TRANSCRIPT_TYPES.SCENE_CONVERSION_MODAL_VISIBLE,
);

export const storeTranscriptTranslations = createAction<{
    sceneGroupId: string;
    translations: ImmutableMap<TranscriptTranslations>;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.STORE_TRANSCRIPT_TRANSLATIONS);

export const removeTranscriptTranslation = createAction<{
    sceneGroupId: string;
    languageCode: string;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(TRANSCRIPT_TYPES.REMOVE_TRANSCRIPT_TRANSLATION);

/**
 * This action only sets the current time and sceneGroupId of the transcript. It does not
 * update the corresponding preview. If you want both time and the preview to update
 * then use updateTranscriptCurrentTimeAndPreview.
 */
export const setTranscriptCurrentTime = createAction<{
    sceneGroupId: string | null;
    sceneId: string | null;
    currentTimeMillis: number;
}>(TRANSCRIPT_TYPES.SET_CURRENT_TIME);

export const storeLexicalEditor = createAction<LexicalEditorsDataType>(
    TRANSCRIPT_TYPES.STORE_LEXICAL_EDITOR,
);
export const removeLexicalEditor = createAction<string>(TRANSCRIPT_TYPES.REMOVE_LEXICAL_EDITOR);
export const overwriteAllLexicalEditors = createAction<LexicalEditorsDataType[]>(
    TRANSCRIPT_TYPES.OVERWRITE_ALL_LEXICAL_EDITORS,
);
type LexicalControllingItsOwnStatePayload = {
    sceneGroupId: string;
    langCode?: string; // if langCode is not provided, we assume it's the "main" transcript (not one of the translation transcripts)
};
export const startLexicalControllingItsOwnState =
    createAction<LexicalControllingItsOwnStatePayload>(
        TRANSCRIPT_TYPES.START_LEXICAL_CONTROLLING_ITS_OWN_STATE,
    );
export const endLexicalControllingItsOwnState = createAction<LexicalControllingItsOwnStatePayload>(
    TRANSCRIPT_TYPES.END_LEXICAL_CONTROLLING_ITS_OWN_STATE,
);
